import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { baseFetch } from "../helpers/apiClient";
import {
  setLoadingState,
  setFulfilledState,
  setRejectedState,
} from "../helpers/authHelper.js";
import { toggleSessionModal } from "./sessionSlice.js";
import { normalizeProfile } from "../helpers/normalizeProfile.js";

let profileRequest = null;
let profileRequestGeneration = 0;

const invalidateProfileRequest = () => {
  profileRequestGeneration += 1;
  profileRequest = null;
};

const requestProfile = () => {
  const token = window.localStorage.getItem("token") ?? null;

  if (!profileRequest || profileRequest.token !== token) {
    const generation = ++profileRequestGeneration;
    const request = baseFetch({
      url: "/v2/me/profile",
      method: "GET",
    });
    profileRequest = { token, promise: request, generation };
    request.then(
      () => {
        if (profileRequest?.promise === request) profileRequest = null;
      },
      () => {
        if (profileRequest?.promise === request) profileRequest = null;
      },
    );
  }
  return profileRequest;
};

/**
 * The signed-in member's profile.
 *
 * Reads the canonical `GET /v2/me/profile` contract (ECH-393), not the legacy
 * `POST /v1/web/profile/register/status` envelope. The V2 response keeps
 * profile-level data and typed demographics separate, so consumers do not need
 * a client-side legacy role-profile reconstruction.
 *
 * Twelve places dispatch this thunk. Moving the one request fixes all of them.
 *
 * No body. The server resolves the caller from the token.
 */
export const fetchProfileInfo = createAsyncThunk(
  "profile/status",
  async (_, { dispatch, fulfillWithValue, rejectWithValue }) => {
    const request = requestProfile();
    try {
      const response = await request.promise;
      return fulfillWithValue(response.data ?? response, {
        profileRequestGeneration: request.generation,
      });
    } catch (error) {
      if (
        error === "Please authenticate" &&
        request.generation === profileRequestGeneration
      ) {
        dispatch(toggleSessionModal(false));
      }
      return rejectWithValue(error || "Something went wrong", {
        profileRequestGeneration: request.generation,
      });
    }
  },
);
export const initialAuthState = {
  profile: null,
  profileStatus: null,
  profileError: null,
  updatedFields: {},
  profileStep: 0,
};
const profileReducer = createSlice({
  name: "profile",
  initialState: initialAuthState,
  reducers: {
    clearProfileData: (state) => {
      invalidateProfileRequest();
      state.profile = null;
      state.profileStatus = null;
      state.profileError = null;
      state.updatedFields = {};
    },
    updateProfileData: (state, action) => {
      state.profile.profile = {
        ...state.profile.profile,
        ...action.payload,
      };
    },
    clearUpdatedFields: (state) => {
      state.updatedFields = {};
    },
    updateProfileStep: (state, action) => {
      state.profileStep = action.payload;
    },
    updateFields: (state, action) => {
      const { field, value } = action.payload;
      console.log("field is", field);
      console.log("value is", value);
      if (field.includes(".")) {
        const keys = field.split(".");
        let current = state.updatedFields;
        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          if (typeof current[key] !== "object" || current[key] === null) {
            current[key] = {};
          }
          current = current[key];
        }
        current[keys[keys.length - 1]] = value;
        console.log("current is", current);
        console.log("key is", keys[keys.length - 1]);
        console.log("value after updating", value);
      } else {
        state.updatedFields[field] = value;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfileInfo.pending, (state) =>
        setLoadingState(state, "profile"),
      )
      .addCase(fetchProfileInfo.fulfilled, (state, action) => {
        if (action.meta.profileRequestGeneration !== profileRequestGeneration) {
          return;
        }
        setFulfilledState(state, "profile", normalizeProfile(action.payload));
      })
      .addCase(fetchProfileInfo.rejected, (state, action) => {
        if (action.meta.profileRequestGeneration !== profileRequestGeneration) {
          return;
        }
        setRejectedState(state, "profile", action.error);
      });
  },
});

export const {
  clearProfileData,
  updateProfileData,
  updateFields,
  clearUpdatedFields,
  updateProfileStep,
} = profileReducer.actions;

export default profileReducer.reducer;
